import React from 'react';
import { View, StyleSheet, ViewStyle } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

interface GradientBorderFrameProps {
  children: React.ReactNode;
  style?: ViewStyle;
  innerStyle?: ViewStyle;
  borderWidth?: number;
  borderRadius?: number;
  padding?: number;
  isInnerFrame?: boolean;
}

/**
 * Renders the brand-defining blue → purple → red gradient border with a
 * theme-aware inner surface. The gradient stops themselves are intentional
 * brand moments and stay constant across themes; only the inner fill picks up
 * the active admin surface so light-mode users see white/frosted instead of
 * a flat dark panel.
 */
export const GradientBorderFrame: React.FC<GradientBorderFrameProps> = ({
  children,
  style,
  innerStyle,
  borderWidth = 3,
  borderRadius = 32,
}) => {
  const styles = useStyles();
  const gradientColors = [
    PRIMITIVE_COLORS.resioBlueBorder,
    PRIMITIVE_COLORS.resioPurple,
    PRIMITIVE_COLORS.resioPink,
  ];

  const gradientLocations = [0.1138, 0.6385, 0.9773];

  const edge = Math.max(1, Math.floor(borderWidth));
  const gradientBorderRadius = borderRadius;
  const innerBorderRadius = Math.max(0, gradientBorderRadius - edge);

  return (
    <View style={[{ flex: 1, width: '100%' }, style]}>
      <LinearGradient
        colors={gradientColors}
        locations={gradientLocations}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={[
          styles.gradientContainer,
          {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderTopLeftRadius: gradientBorderRadius,
            borderTopRightRadius: gradientBorderRadius,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        ]}
      />
      <View
        style={[
          styles.innerContainer,
          {
            borderTopLeftRadius: innerBorderRadius,
            borderTopRightRadius: innerBorderRadius,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            flex: 1,
            marginTop: edge,
            marginLeft: edge,
            marginRight: edge,
            marginBottom: 0,
          },
          innerStyle,
        ]}
      >
        {children}
      </View>
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components }) => {
    const admin = components.admin;
    return StyleSheet.create({
      gradientContainer: {},
      innerContainer: {
        flex: 1,
        backgroundColor: admin.dataTable.bg,
      },
    });
  });
